import {
  Box,
  Flex,
  LinkButton,
  DotsLoader,
  ScreenReaderOnly,
  Text,
} from '@chilipiper/design-system'
import { parseQuery, getDomain } from '@chilipiper/utils'
import { defaultLocale, useLocale, useTranslations } from '@chilipiper/locale'
import { useAvailabilityContext, BookWith } from '@chilipiper/booking-flow-legacy'
import strategy from '@chilipiper/deprecated-validatorjs'
import { getDateTimeFormat } from '@chilipiper/models/src/getDateTimeFormat'
import { defaultErrorCallback, get, post, rootApiPost } from '@chilipiper/service/src/api'
import classnames from 'classnames'
import { mapValues } from 'lodash'
import cloneDeep from 'lodash/cloneDeep'
import find from 'lodash/find'
import moment from 'moment-timezone'
import React from 'react'
import validation from 'react-validation-mixin'
import isString from 'lodash/isString'
import { logRocketTrack } from '@chilipiper/tracking'
import { GuestForms } from './GuestForms'
import { createProspectForm, parseProspectName } from './helpers'

class ProspectFormWithoutLocale extends React.Component {
  q = parseQuery()
  cameFromCompose = this.q.cameFromCompose === 'true'

  state = {
    loading: true,
    submitting: false,
    open: false,
    isDirty: false,
    additionalGuestsOpen: false,
    forcedAddAdditionalGuest: false,
    prospect: {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    error: false,
  }

  getValidatorData = () => this.prospectValidatorData()

  componentDidMount = () => {
    if (this.cameFromCompose) {
      const event = 'Selected Suggested Time'
      logRocketTrack(event)
    }
    if (this.props.guests?.length) {
      this.setState({ additionalGuestsOpen: true })
    }
    this.UNSAFE_componentWillReceiveProps(this.props)
  }

  UNSAFE_componentWillReceiveProps = props => {
    if (!this.state.isDirty) {
      this.describeSalesObject('Lead', () => {
        this.describeSalesObject('Contact', () => {
          this.setState({ isDirty: true })
          this.setState(props, this.loadForm)
        })
      })
    }
  }

  loadRouterLogo = () => {
    get(`online/logo-by-router/${getDomain()}/${this.state.routerName}`)
      .json()
      .then(logo => {
        if (logo && logo.length > 0) {
          this.setState({ logo: logo })
        }
      })
      .catch(defaultErrorCallback)
  }

  describeSalesObject = (name, callback) => {
    const endpoint = `online/prospect/describe/${getDomain()}/${name}`
    get(endpoint)
      .json()
      .then(fields => {
        const state = {}
        state[`${name.toLowerCase()}Fields`] = fields
        if (fields.length) {
          state.isHubspot = fields[0].crmType === 'Hubspot'
        }
        this.setState(state, callback)
      })
      .catch(response => {
        this.props.onFatalError({
          response: response,
          endpoint: endpoint,
        })
      })
  }

  createSchema = () => {
    const state = this.state
    const blocks = state.form.blocks
    const schema = {}
    const messages = {}

    blocks.forEach(block => {
      const field =
        find(state.leadFields, f => f.name === block.leadSFField) ||
        find(state.contactFields, f =>
          state.isHubspot ? f.name === block.contactHSField : f.name === block.contactSFField
        ) ||
        {}

      schema[block.fieldName] =
        block.required && !block.hidden && block.fieldName ? ['required'] : []

      messages[`required.${block.fieldName}`] =
        (block.locales[this.props.locale] ?? block.locales[defaultLocale]).error ||
        `${block.label} is required`
      if (field.types === 'email' || block.fieldName === 'email') {
        schema[block.fieldName].push('email')
      } else if (field.types === 'phone' && block.restricted) {
        schema[block.fieldName].push('regex:/(^\\d{8,}$)|(^\\+\\d{8,}$)/')
      } else if (field.types === 'int') {
        schema[block.fieldName].push('integer', 'between:1,9999999')
      }
    })

    this.validatorTypes = strategy.createSchema(schema, messages)
  }

  prospectValidatorData = () => {
    const state = this.state
    const prospect = cloneDeep(state.prospect)
    const keys = Object.keys(prospect)
    keys.forEach(key => {
      const block = find(state.form.blocks, b => b.fieldName === key)
      if (block) {
        const field =
          find(state.leadFields, f => f.name === block.leadSFField) ||
          find(state.contactFields, f => f.name === block.contactSFField) ||
          {}
        if (field.types === 'phone' && block.restricted && prospect[key]) {
          prospect[key] = prospect[key].replace(/[ -.]/g, '')
        }
      }
    })

    return prospect
  }

  getFormLink = () => {
    const template = this.state.templateId
    if (this.state.routerName) {
      return `online/prospect-by-router/${getDomain()}/${this.state.routerName}`
    }
    if (template) {
      return `online/prospect-by-template/${getDomain()}/${template}`
    }
    return `online/prospect-default-form/${getDomain()}`
  }

  getSaveLink = () => {
    const form = this.state.form.id
    const booker = this.state.booker
    const templateId = this.state.templateId
    const { campaignId, status } = this.q

    let url = `online/prospect-by-form/${getDomain()}/${form}/${booker}`

    if (campaignId) {
      url += `?campaignId=${campaignId}${status ? `&memberStatus=${status}` : ''}`
    }

    if (templateId) {
      url += `${url.includes('?') ? '&' : '?'}templateId=${templateId}`
    }

    return url
  }

  loadForm = () => {
    const endpoint = this.getFormLink()
    if (!this.state.pickHeader) {
      this.loadRouterLogo()
    }
    get(endpoint)
      .json()
      .then(form => {
        const { prospect } = this.state
        if (this.props.email && prospect.email === undefined) {
          prospect.email = this.props.email
        }
        this.setState({ form: form, prospect: prospect, loading: false }, () => {
          this.createSchema()
          this.setDefaultFields()
        })
      })
      .catch(response => {
        this.props.onFatalError({
          response: response,
          endpoint: endpoint,
        })
      })
  }

  setDefaultFields = () => {
    const { form, prospect: stateProspect, intercomData } = this.state
    form.blocks.forEach(block => {
      const localValue =
        this.q.debugPersist &&
        localStorage &&
        localStorage.getItem(`chili-prospect-${block.fieldName}`)
      const prospect = cloneDeep(stateProspect)
      const getParams = {}
      Object.keys(this.q).forEach(key => (getParams[key.toLowerCase()] = this.q[key]))
      if (intercomData && intercomData[block.fieldName]) {
        prospect[block.fieldName] = intercomData[block.fieldName]
      } else if (getParams[block.fieldName.toLowerCase()]) {
        prospect[block.fieldName] = getParams[block.fieldName.toLowerCase()]
      } else {
        prospect[block.fieldName] = prospect[block.fieldName] || block.defaultValue || localValue
      }
      this.setState({ prospect: prospect })
    })
  }

  onConfirm = () => {
    if (this.state.loading || this.state.submitting) {
      return
    }
    if (this.cameFromCompose) {
      const event = 'Booked with Suggested Times'
      logRocketTrack(event)
    }

    const { pageSource } = parseQuery()

    if (isString(pageSource) && pageSource === 'personal-page') {
      const event = 'Booked meeting from Personal Page'
      logRocketTrack(event)
    }

    this.saveProspect()
  }

  transformUrl = url => {
    const urlLink = new URL(url)

    const shouldAddDomain = window.location.origin !== urlLink.origin
    const domainParam = urlLink.search ? `&domain=${getDomain()}` : `?domain=${getDomain()}`

    return `${window.location.origin}${urlLink.pathname}${urlLink.search}${
      shouldAddDomain ? domainParam : ''
    }`
  }

  onBack = () => {
    this.props.onConfirm(false)
    this.setState({ open: false, isDirty: false })
  }

  handleProspectFormValidationError = error => {
    logRocketTrack('Prospect form is not valid', {
      domain: getDomain(),
      prospect: this.state.prospect,
      error,
    })
  }

  handleRouterLinkUpdate = result => {
    // for the router the result is object with URL and Redirect
    if (process.env.NODE_ENV === 'development') {
      if (result.realtimeUrl) {
        result.realtimeUrl = this.transformUrl(result.realtimeUrl)
      }
      if (result.url) {
        result.url = this.transformUrl(result.url)
      }
    }

    this.props.onRouterLink(result)
  }

  saveProspect = () => {
    const { map } = this.q
    const prospect = parseProspectName(this.state.prospect, this.state.form.default)
    const prospectForm = createProspectForm({
      prospect,
      prospectId: this.state.prospectId,
      defaultForm: this.state.form.default,
    })

    this.props.validate(error => {
      if (error) {
        this.handleProspectFormValidationError(error)
        return
      }

      this.setState({ submitting: true })

      const { by } = this.q
      if (this.state.routerName) {
        const link = `marketing/${getDomain()}${by ? `?by=${by}` : ''}`
        rootApiPost(link, {
          json: {
            form: mapValues(prospectForm.values, ([value]) => value),
            options: {
              router: this.state.routerName,
              map: map === 'false' ? false : true,
            },
          },
        })
          .json()
          .then(result => this.handleRouterLinkUpdate(result))
          .catch(e => {
            console.error(e)
            defaultErrorCallback(e)
            this.props.onConfirm(true, prospect, this.getPickedMeetingLocation())
          })
      } else {
        let link = this.getSaveLink()
        link = by ? `${link}${link.includes('?') ? '&' : '?'}by=${by}` : link

        post(link, {
          json: prospectForm,
        })
          .json()
          .then(result => {
            if (result && result.id !== '') {
              prospect.id = result.id
            }
            prospect.marketingId = (result || {}).marketingId
            this.props.onConfirm(true, prospect, this.getPickedMeetingLocation())
          })
          .catch(e => {
            console.error(e)
            defaultErrorCallback(e)
            this.props.onConfirm(true, prospect, this.getPickedMeetingLocation())
          })
      }
    })
  }

  getPickedMeetingLocation = () => {
    const { possibleMeetingLocations = [] } = this.props.template || {}

    if (possibleMeetingLocations.length > 1 && this.state.pickedMeetingLocation) {
      return this.state.pickedMeetingLocation
    }

    return possibleMeetingLocations.find(location => location.isDefault)
  }

  getDateDisplay = () => {
    if (!this.state.time || this.props.seriesId || this.props.hideTime) {
      return null
    }
    const { locale } = this.props
    let start = this.props?.bookMeetingTimezone
      ? moment.tz(this.state.time, this.props.bookMeetingTimezone)
      : moment(this.state.time)
    start = start.locale(locale)
    const end = moment(start).add(this.state.step, 'minutes')
    const getDateTime = getDateTimeFormat(locale)
    const dateFormat = locale === 'en_US' ? 'LT' : 'HH:mm'

    return {
      date: start.format(getDateTime('dddd, MMM Do YYYY')),
      timeInterval: `${start.format(dateFormat)} - ${end.format(dateFormat)}`,
    }
  }

  renderTime() {
    if (!this.state.time || this.props.seriesId || this.props.hideTime) {
      return null
    }
    const { isDialog, hideBack } = this.props
    const { date, timeInterval } = this.getDateDisplay()

    return (
      <Box mb={6} mt={isDialog ? 12 : undefined} w='100%'>
        <Box mb={3}>
          <Text data-chili-css='almost-there' textStyle='modal-heading' m={0} lineHeight='100%'>
            {this.props.almostThereText}
          </Text>
        </Box>
        <Text data-chili-css='meeting-info' color='neutral-dark-80' fontWeight={500}>
          <span data-test-id='ProspectForm-meeting-time'>
            {date} {timeInterval}
          </span>
          {` `}
          <span data-test-id='ProspectForm-timezone'>
            {this.props.eventTimezone ? ` ${this.props.eventTimezone}` : ''}
          </span>
        </Text>
        {this.onBack && !hideBack ? (
          <LinkButton
            data-chili-css='change-link'
            onClick={this.onBack}
            m={0}
            px={0}
            color='neutral-dark-80'
            fontWeight={400}
            data-test-id='change-booking-time-button'
          >
            {this.props.changeText}
          </LinkButton>
        ) : null}
      </Box>
    )
  }

  renderGuestForms = () => {
    return (
      <div
        className={`fabe-confirm-book-dialog ${
          this.state.prospectId ? 'fabe-with-prospect' : null
        } `}
      >
        {this.renderTime()}
        <GuestForms
          leadFields={this.state.leadFields}
          contactFields={this.state.contactFields}
          isHubspot={this.state.isHubspot}
          form={this.state.form}
          prospect={this.state.prospect}
          buttonLabel={<ButtonLabel isSubmit={this.state.creation} />}
          onConfirm={this.onConfirm}
          onChange={this.onGuestFormsChange}
          guests={this.props.guests}
          pickedMeetingLocation={this.state.pickedMeetingLocation}
          updateGuests={this.props.updateGuests}
          additionalGuestsOpen={this.state.additionalGuestsOpen}
          setAdditionalGuestsOpen={isOpen => this.setState({ additionalGuestsOpen: isOpen })}
          onMeetingLocationChange={this.onMeetingLocationChange}
          locations={
            this.props.possibleMeetingLocations ?? this.props.template?.possibleMeetingLocations
          }
          isRouterPage={!!this.state.routerName}
        />
      </div>
    )
  }

  renderBookWith = () => (
    <BookWith
      template={this.state.template}
      showBack={this.state.showBack}
      isRescheduling={this.state.isRescheduling}
      assigneeId={this.state.bookme?.bookMeeting?.assigneeId ?? this.state.bookme?.assigneeId}
      assigneeName={this.state.assigneeName}
      queue={this.state.queue}
      hideBack={this.props.hideBack}
      onBack={this.onBack}
    />
  )

  renderRouterLogo = () => (
    <div className='fabe-company-logo-original-size'>
      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- new rule */}
      <img src={this.state.logo} crossOrigin='anonymous' alt='' />
    </div>
  )

  onGuestFormsChange = fields => {
    const state = { ...this.state }

    state.prospect = {
      ...state.prospect,
      ...fields,
    }

    if (this.q.debugPersist && localStorage) {
      Object.entries(fields).forEach(([key, value]) =>
        localStorage.setItem(`chili-prospect-${key}`, value)
      )
    }

    state.isDirty = true
    this.setState(state)

    if (this.props.email) {
      this.updateAdditionalGuestsField(state.prospect.email)
    }
  }

  updateAdditionalGuestsField = prospectEmail => {
    if (this.props.email === prospectEmail) {
      this.removeGuest(prospectEmail)
      this.setState(prevState => ({ ...prevState, forcedAddAdditionalGuest: false }))
    } else if (
      !this.state.forcedAddAdditionalGuest &&
      !this.props.guests.includes(this.props.email)
    ) {
      this.addGuest(this.props.email)
      this.setState(prevState => ({
        ...prevState,
        additionalGuestsOpen: true,
        forcedAddAdditionalGuest: true,
      }))
    }
  }

  removeGuest = guestEmail => {
    this.props.updateGuests(this.props.guests.filter(email => email !== guestEmail))
  }
  addGuest = guestEmail => {
    this.props.updateGuests(this.props.guests.concat(guestEmail))
  }

  onMeetingLocationChange = pickedMeetingLocation => {
    this.setState({
      pickedMeetingLocation,
    })
  }

  render() {
    const { loading, submitting, logo } = this.state
    const { intercomData, pickHeader } = this.props
    if (loading || submitting) {
      return <Loader message={`${loading ? 'Loading' : 'Submitting'} form, please wait...`} />
    }
    const className = classnames('fabe-mobile-confirmation', {
      'inside-intercom': intercomData,
    })
    return (
      <div data-chili-css='page' data-test-id='ProspectFormWithValidation' className={className}>
        <ScreenReaderOnly role='status'>
          Please, fill out the form before booking the meeting
        </ScreenReaderOnly>
        {this.props.customHeader}
        {pickHeader && this.renderBookWith()}
        {!pickHeader && logo && this.renderRouterLogo()}
        {this.renderGuestForms()}
      </div>
    )
  }
}

const ProspectFormWithoutLocaleWithValidation = validation(strategy)(ProspectFormWithoutLocale)
export const ProspectFormWithValidation = props => {
  const locale = useLocale()
  const [almostThereText, changeText] = useTranslations(['almostThere', 'change'])
  const { setAvailabilityState } = useAvailabilityContext()
  return (
    <ProspectFormWithoutLocaleWithValidation
      locale={locale}
      onFatalError={setAvailabilityState}
      almostThereText={almostThereText}
      changeText={changeText}
      {...props}
    />
  )
}

const ButtonLabel = ({ isSubmit }) => {
  const [submitButton, confirmButton] = useTranslations(['submitButton', 'confirmButton'])
  return isSubmit ? submitButton : confirmButton
}

const Loader = ({ message }) => {
  const [pleaseWait] = useTranslations(['pleaseWait'])
  return (
    <Flex
      height='100%'
      minHeight='90vh'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
    >
      <ScreenReaderOnly role='status'>{message}</ScreenReaderOnly>
      <DotsLoader color='neutral-dark-32' />
      <Text mt={4}>{pleaseWait}</Text>
    </Flex>
  )
}

export default ProspectFormWithValidation
